import * as React from 'react';

import { Alert } from 'antd';

import { useFormikContext } from 'formik';

type StepperContextProps = {
  currentStep: number;
  isLast: boolean;
  isFirst: boolean;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  stepsLength: number;
};

const StepperContext = React.createContext<StepperContextProps | null>(null);

type StepperState = {
  currentStep: number;
};

enum StepperActionType {
  NEXT_STEP,
  PREV_STEP,
  GO_TO_STEP
}

type StepperAction = {
  type: StepperActionType;
  step?: number;
};

type ErrorProps = {
  step: number;
  description: string;
};

function stepperReducer(state: StepperState, { type, step }: StepperAction) {
  switch (type) {
    case StepperActionType.NEXT_STEP: {
      return {
        ...state,
        currentStep: state.currentStep + 1
      };
    }

    case StepperActionType.PREV_STEP: {
      return {
        ...state,
        currentStep: state.currentStep - 1
      };
    }

    case StepperActionType.GO_TO_STEP: {
      return {
        ...state,
        currentStep: step
      };
    }

    default:
      throw new Error(`Stepper action ${type} not found`);
  }
}

type StepperProviderProps = {
  steps: React.ReactElement[];
  stepsLength?: number;
  initialStep?: number;
  children?: React.ReactNode;
  error: ErrorProps | null;
};

const FIRST_STEP_INDEX = 1;
const VERIFY_STEP_INDEX = 5;

const StepperProvider = ({
  children,
  error,
  initialStep = FIRST_STEP_INDEX,
  steps,
  stepsLength = steps.length
}: StepperProviderProps) => {
  const [state, dispatch] = React.useReducer(stepperReducer, {
    currentStep: initialStep
  });

  const nextStep = React.useCallback(() => {
    dispatch({ type: StepperActionType.NEXT_STEP });
  }, []);

  const prevStep = React.useCallback(() => {
    dispatch({ type: StepperActionType.PREV_STEP });
  }, []);

  const goToStep = React.useCallback((step: number) => {
    dispatch({ type: StepperActionType.GO_TO_STEP, step });
  }, []);

  const ctxValue = React.useMemo<StepperContextProps>(
    () => ({
      ...state,
      stepsLength,
      isFirst: state.currentStep === FIRST_STEP_INDEX,
      isLast: state.currentStep === stepsLength,
      nextStep,
      prevStep,
      goToStep
    }),
    [goToStep, nextStep, prevStep, state, stepsLength]
  );

  const formik = useFormikContext();
  const { currentStep } = state;

  React.useEffect(() => {
    if (formik?.status === 'submitted') goToStep(VERIFY_STEP_INDEX);
    if (formik?.status === 'verified' && currentStep !== stepsLength) goToStep(stepsLength);
  }, [formik, currentStep, stepsLength, goToStep]);

  React.useEffect(() => {
    if (sessionStorage.step_error) {
      goToStep(parseInt(sessionStorage.step_error));
      sessionStorage.removeItem('step_error');
    }
  }, [goToStep]);

  return (
    <StepperContext.Provider value={ctxValue}>
      {children}
      {!!error && currentStep === error?.step && <Alert type='warning' message={error.description} />}
      {steps[currentStep - 1]}
    </StepperContext.Provider>
  );
};

function useStepper() {
  const ctx = React.useContext(StepperContext);

  if (!ctx) {
    throw new Error('You must use StepperContext within StepperProvider');
  }

  return ctx;
}

export { StepperProvider, useStepper };

StepperContext.displayName = 'StepperContext';
